/*
 * Ultimate Bundle landing page: hero gallery, plan picker and add to cart.
 * Added 2026-10-12. Used by sections/ub-hero.liquid (and the "Add to cart"
 * button in sections/ub-lifestyle.liquid).
 *
 * Everything is scoped to the <ub-hero-root> element, so it never picks up
 * another product form on the page (unlike the document-level
 * quantityInput in product-buy-buttons.js).
 *
 * Add to cart posts to /cart/add.js directly (2026-10-12), then refreshes the
 * theme cart (assets/cart.js) and opens the cart drawer like the regular
 * product page. The buy box is deliberately NOT a /cart/add form: Timesact and
 * Seal inject their own name="selling_plan" radios into such forms, which
 * hijacked this plan picker. Shopify's error text is shown if an add fails.
 */
(function () {
  if (customElements.get('ub-hero-root')) return;

  class UbHero extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-ub-form]');
      this.submitButton = this.querySelector('[data-ub-submit]');
      this.submitText = this.querySelector('[data-ub-submit-text]');
      this.errorEl = this.querySelector('[data-ub-error]');
      this.variantInput = this.querySelector('[data-ub-variant]');

      try {
        this.data = JSON.parse(this.querySelector('[data-ub-data]').textContent);
      } catch (e) {
        this.data = { planId: null, text: {}, variants: {} };
      }

      this.setupGallery();
      this.setupPlans();
      this.setupVariants();
      if (this.submitButton) {
        this.submitButton.addEventListener('click', () => this.addToCart());
      }
    }

    /* ---------- Gallery ---------- */
    setupGallery() {
      this.thumbs = Array.from(this.querySelectorAll('[data-ub-thumb]'));
      this.slides = Array.from(this.querySelectorAll('[data-ub-slide]'));
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showSlide(thumb.dataset.ubThumb));
      });
    }

    showSlide(index) {
      this.slides.forEach((slide) => slide.classList.toggle('is-active', slide.dataset.ubSlide === index));
      this.thumbs.forEach((thumb) => thumb.setAttribute('aria-current', String(thumb.dataset.ubThumb === index)));
    }

    /* ---------- Plan picker ---------- */
    setupPlans() {
      this.planInputs = Array.from(this.querySelectorAll('[data-ub-plan]'));
      this.planInputs.forEach((input) => {
        input.addEventListener('change', () => this.updatePlan());
      });
      this.updatePlan();
    }

    selectedPlan() {
      const checked = this.planInputs.find((input) => input.checked);
      return checked ? checked.dataset.ubPlan : 'onetime';
    }

    updatePlan() {
      const plan = this.selectedPlan();
      this.dataset.plan = plan;
      this.querySelectorAll('[data-ub-plan-card]').forEach((card) => {
        card.classList.toggle('is-selected', card.dataset.ubPlanCard === plan);
      });
      this.updateButton();
    }

    /* ---------- Variants (only when the product has more than one) ---------- */
    setupVariants() {
      if (!this.variantInput || this.variantInput.tagName !== 'SELECT') return;
      this.variantInput.addEventListener('change', () => {
        const variant = this.currentVariant();
        if (!variant) return;
        const planPrice = this.querySelector('[data-ub-plan-price]');
        const pct = this.querySelector('[data-ub-plan-pct]');
        const onetime = this.querySelector('[data-ub-onetime-price]');
        if (planPrice) planPrice.textContent = variant.planPrice;
        if (onetime && !this.data.onetime) onetime.textContent = variant.price;
        if (pct) {
          pct.hidden = !variant.pct;
          pct.textContent = (this.data.text.pct || '[pct]% off').replace('[pct]', variant.pct) + ' · ';
        }
        this.updateButton();
      });
    }

    currentVariant() {
      if (!this.variantInput) return null;
      return this.data.variants[this.variantInput.value] || null;
    }

    updateButton() {
      if (!this.submitButton || !this.submitText) return;
      // Works for both the hidden single-variant input and the variant <select>.
      // One-time may add a separate (giftless) product: use its stock instead.
      const text = this.data.text || {};
      // Offer mode (Cart line blocks): availability was worked out per plan in Liquid.
      if (this.data.offer) {
        const ok = this.selectedPlan() === 'subscribe' ? this.data.offer.okSub : this.data.offer.okOne;
        this.submitButton.disabled = !ok;
        this.submitText.textContent = !ok ? text.soldOut || '' : this.selectedPlan() === 'subscribe' ? text.subscribe : text.onetime;
        return;
      }
      const variant = this.selectedPlan() === 'onetime' && this.data.onetime ? this.data.onetime : this.currentVariant();
      if (variant && !variant.available) {
        this.submitButton.disabled = true;
        this.submitText.textContent = text.soldOut || '';
        return;
      }
      this.submitButton.disabled = false;
      this.submitText.textContent = this.selectedPlan() === 'subscribe' ? text.subscribe : text.onetime;
    }

    /* ---------- Add to cart ---------- */
    async addToCart() {
      if (!this.submitButton || this.submitButton.disabled) return;
      const plan = this.selectedPlan();
      const sellingPlan = plan === 'subscribe' && this.data.planId ? Number(this.data.planId) : null;
      const variantId = this.data.offer
        ? 'offer'
        : plan === 'onetime' && this.data.onetime
          ? this.data.onetime.id
          : this.variantInput
          ? this.variantInput.value
          : null;
      if (!variantId) return;

      const item = { id: Number(variantId), quantity: 1 };
      if (sellingPlan) item.selling_plan = sellingPlan;
      const items = this.data.offer ? this.offerItems(plan) : [item];
      if (!items.length) return;

      this.showError('');
      // 2026-10-15: keep Rebuy's cart invisible until settleOfferCart() has removed its gifts (see ub-bundle.css).
      /* 2026-10-15 (later): Rebuy's gifts (Duo Pads, free shipping) are now PART of the offer, so we no
         longer hide Rebuy's cart or strip its gifts. Kept for undo:
      if (this.data.offer) document.documentElement.classList.add('ub-cart-settling');
      */
      // 2026-10-15: hide Rebuy's gift row until Rebuy has made it free (see watchRebuyGifts, ub-bundle.css).
      if (this.data.offer) document.documentElement.classList.add('ub-gift-pending');
      this.submitButton.setAttribute('data-loading', 'true');
      this.submitButton.disabled = true;

      try {
        const base = (window.theme && theme.urls && theme.urls.cart_add) || '/cart/add';
        const response = await fetch(base + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const error = new Error(data.description || data.message || response.statusText);
          error.fromShopify = true;
          throw error;
        }
        /* 2026-10-15 (later): disabled, it would remove Rebuy's free Duo Pads. Kept for undo:
        if (this.data.offer) await this.settleOfferCart();
        */
        document.documentElement.classList.remove('ub-cart-settling');
        if (this.data.offer) this.watchRebuyGifts();
        await this.afterAdd(items[0].id);
      } catch (error) {
        document.documentElement.classList.remove('ub-gift-pending');
        const text = (this.data.text && this.data.text.error) || '';
        this.showError(error && error.fromShopify && error.message ? `${text} (${error.message})` : text);
        console.error('[ub-hero] add to cart failed', items, error);
      } finally {
        document.documentElement.classList.remove('ub-cart-settling');
        this.submitButton.removeAttribute('data-loading');
        this.submitButton.disabled = false;
        this.updateButton();
      }
    }

    /* Offer mode (2026-10-12): the cart lines from the section's Cart line blocks.
       Subscribe = paid lines with their Seal plan + gift lines (no plan; skipped if sold out).
       One-time  = paid lines only, no plan. Every line carries _offer = tag (for Rebuy). */
    offerItems(plan) {
      const offer = this.data.offer;
      const tag = offer.tag || 'UCGC';
      const items = [];
      /* 2026-10-15: the Welcome Kit discount only makes the gifts free when ALL of them are in the
         cart ("get 2 items"). If one gift is unavailable, adding the others would charge the
         customer for a "free" gift (seen: pouch unavailable → dispenser $19.99). So: all or none. */
      const giftLines = offer.lines.filter((line) => line.role === 'gift');
      const giftsOk = giftLines.every((line) => line.available);
      if (plan === 'subscribe' && !giftsOk) {
        console.warn('[ub-hero] a gift is unavailable, no gifts added:',
          giftLines.filter((line) => !line.available).map((line) => line.sku).join(', '));
      }
      offer.lines.forEach((line) => {
        const properties = { _offer: tag };
        if (line.role === 'gift') {
          if (plan !== 'subscribe') return;
          if (!giftsOk) return;
          if (!line.available) {
            console.warn('[ub-hero] gift sold out, not added', line.sku);
            return;
          }
          properties._offer_gift = 'true';
          items.push({ id: Number(line.id), quantity: 1, properties });
          return;
        }
        const item = { id: Number(line.id), quantity: 1, properties };
        if (plan === 'subscribe' && line.plan) item.selling_plan = Number(line.plan);
        items.push(item);
      });
      return items;
    }

    /* 2026-10-14: Rebuy adds its own free gifts (Duo Pads, Travel Pouch) right after our add.
       They showed in the drawer for about a second (total ~$88, dispenser at full price) until
       the Rebuy Show callback removed them. Remove them here, BEFORE the drawer opens, so the
       first total shown is the real one. Gift = a line without our _offer tag that costs $0.
       Waits at least ~1.2 s and at most ~3.5 s; the button keeps its loading state meanwhile.
       The Rebuy Show callback stays as a backup. */
    async settleOfferCart() {
      const started = Date.now();
      const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
      let quiet = 0;
      while (Date.now() - started < 3500) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        let cart;
        try {
          cart = await fetch('/cart.js', { headers }).then((r) => r.json());
        } catch (error) {
          return;
        }
        const gifts = (cart.items || []).filter(
          (i) => !(i.properties && i.properties._offer) && i.final_line_price === 0
        );
        if (!gifts.length) {
          quiet += 1;
          if (quiet >= 3 && Date.now() - started >= 1200) return;
          continue;
        }
        quiet = 0;
        const updates = {};
        gifts.forEach((i) => { updates[i.key] = 0; });
        try {
          await fetch('/cart/update.js', { method: 'POST', headers, body: JSON.stringify({ updates }) });
        } catch (error) {
          return;
        }
      }
    }

    /* 2026-10-15: Rebuy adds its tier gift (Duo Pads) ~1.7 s after our add at full price ($4.00) and
       only makes it free ~2-3 s later, so the open cart showed "$4.00" for a moment. While
       html.ub-gift-pending is set, ub-bundle.css hides Rebuy's tier-gift rows. The class is removed
       once every such row in Rebuy's cart shows $0.00 (or after 10 s, whatever happens). */
    watchRebuyGifts() {
      const root = document.documentElement;
      const started = Date.now();
      const timer = setInterval(() => {
        const rows = Array.from(
          document.querySelectorAll('.rebuy-cart__flyout-item.property-value-rebuy-tiered-progress-bar')
        );
        const free = rows.length > 0 && rows.every((row) => /(^|[^0-9])0[.,]00(?![0-9])/.test(row.textContent));
        if (free || Date.now() - started > 10000) {
          clearInterval(timer);
          root.classList.remove('ub-gift-pending');
        }
      }, 250);
    }

    /* Refresh the theme cart like theme.cart.addItem does, then open the drawer.
       A failure here must not be reported as a failed add: the item is already in the cart. */
    async afterAdd(variantId) {
      const t = window.theme || {};
      try {
        if (t.cart) {
          if (typeof t.cart.fetchTotals === 'function') t.cart.fetchTotals();
          if (typeof t.cart.updateAllHtml === 'function') await t.cart.updateAllHtml();
        }
        if (typeof window.trigger === 'function') window.trigger('theme:cart:productAdded', variantId);
      } catch (error) {
        console.warn('[ub-hero] cart refresh failed', error);
      }
      /* 2026-10-15: when Rebuy Smart Cart is installed, open ITS cart, not the theme drawer.
         Opening the theme drawer left the theme's dark overlay (with its X) on the page after
         Rebuy's cart was closed. Rebuy's cart brings and removes its own backdrop.
         Falls back to the theme drawer below if Rebuy isn't there. */
      try {
        const rebuyCart = window.Rebuy && window.Rebuy.SmartCart;
        if (rebuyCart && typeof rebuyCart.show === 'function') {
          // 2026-10-15: make Rebuy re-read the cleaned cart before showing it.
          if (window.Rebuy.Cart && typeof window.Rebuy.Cart.fetchCart === 'function') {
            try { await window.Rebuy.Cart.fetchCart(); } catch (error) { /* show anyway */ }
          }
          rebuyCart.show();
          return;
        }
      } catch (error) {
        console.warn('[ub-hero] could not open Rebuy cart', error);
      }
      try {
        if (t.settings && t.settings.cart_type === 'drawer' && t.drawer && typeof t.drawer.open === 'function') {
          t.drawer.open('right', 'cart', this.submitButton);
          return;
        }
      } catch (error) {
        console.warn('[ub-hero] could not open cart drawer', error);
      }
      window.location.href = this.dataset.cartUrl || '/cart';
    }

    showError(message) {
      if (this.errorEl) this.errorEl.textContent = message;
    }

    /* Called by the lifestyle section's "Add to cart" button. */
    addFromProxy() {
      this.addToCart();
    }
  }

  customElements.define('ub-hero-root', UbHero);

  // Theme editor: selecting a "Gallery image" block shows that slide.
  document.addEventListener('shopify:block:select', (event) => {
    const slide = document.querySelector(`ub-hero-root [data-ub-block="${event.detail.blockId}"]`);
    const hero = slide && slide.closest('ub-hero-root');
    if (hero && typeof hero.showSlide === 'function') hero.showSlide(slide.dataset.ubSlide);
  });

  // Other sections on the page (ub-lifestyle) trigger the hero's add to cart.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-ub-atc]');
    if (!trigger) return;
    const hero = document.querySelector('ub-hero-root');
    if (!hero || typeof hero.addFromProxy !== 'function') return; // falls back to the link's href
    event.preventDefault();
    hero.addFromProxy();
  });
})();
